// FILE: src/components/FuneralPlanning.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { db, storage } from "../firebase";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FiEye, FiRefreshCw, FiSearch } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";

// Contacts UI
import {
  useUserContacts,
  groupContactsForOptions,
  useAddContactAutoSelect,
  AddContactModal,
} from "../utils/contacts-ui";

// NEW: popup player (no style change)
import VideoLightbox from "./ui/VideoLightbox";

// NEW: access mode (read-only vs owner)
import { useAccess } from "../access/AccessContext";

// ---------- helpers (TXT builder for email body, no UI change) ----------
function planToText(plan = {}, contacts = []) {
  const nameById = (id) => contacts.find((c) => c.id === id)?.name || id || "";
  const list = (arr) => (arr || []).filter(Boolean).join(", ");
  const listNames = (arr) =>
    (arr || [])
      .map((v) => (typeof v === "object" && v ? v.id : v))
      .filter(Boolean)
      .map(nameById)
      .join(", ");

  return [
    "Funeral Plan",
    `Generated: ${new Date().toLocaleString()}`,
    "",
    `Type of Service: ${plan.typeOfService || ""}`,
    `Venue/Location: ${plan.venue || ""}`,
    `Religious/Cultural Preferences: ${plan.religiousPrefs || ""}`,
    `People to Notify/Involve: ${listNames(plan.notifyContacts)}`,
    `Officiant: ${plan.officiant || ""}`,
    `Readings: ${list(plan.readings)}`,
    `Music: ${list(plan.music)}`,
    `Speakers: ${listNames(plan.speakers)}`,
    `Pallbearers: ${listNames(plan.pallbearers)}`,
    `Flowers/Donations: ${plan.flowersOrDonations || ""}`,
    `Dress Code: ${plan.dressCode || ""}`,
    `Photos / Video Messages: ${plan.photosOrVideos || ""}`,
    `Special Requests: ${plan.specialRequests || ""}`,
    `Ashes Location: ${plan.ashesLocation || ""}`,
    `Inscription: ${plan.graveInscription || ""}`,
    `Ongoing Wishes: ${plan.ongoingWishes || ""}`,
    "",
  ].join("\n");
}

function setAt(arr, i, v) { const a = Array.isArray(arr) ? arr.slice() : []; a[i] = v; return a; }
function removeAt(arr, i) { const a = Array.isArray(arr) ? arr.slice() : []; a.splice(i, 1); return a; }
function toId(v) { return (typeof v === "object" && v ? v.id : v || ""); }
function noteOf(v) { return (typeof v === "object" && v ? (v.note || "") : ""); }

async function loadScript(src) {
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = resolve; s.onerror = () => reject(new Error("Failed to load " + src));
    document.head.appendChild(s);
  });
}
async function ensurePdfLibs() {
  if (!window.html2canvas) await loadScript("https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js");
  if (!window.jspdf && !(window.jsPDF && window.jsPDF.jsPDF)) await loadScript("https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js");
  const jsPDF = window.jspdf?.jsPDF || window.jsPDF?.jsPDF;
  if (!jsPDF) throw new Error("jsPDF not available");
  return { jsPDF };
}

const MEDIA_MAX_SIZE = 50 * 1024 * 1024;
const MEDIA_ACCEPT = /^(image\/|video\/)/i;
const CONTACT_CATEGORIES = ["All", "Family", "Friends", "Work", "School/College", "Community", "Neighbourhood", "Other"];
const DIRECTORY_URL = "https://directory.lastwishbox.com/home-funeral/";

function emptyPlan(uid = "") {
  return {
    uid,
    typeOfService: "",
    venue: "",
    religiousPrefs: "",
    notifyContacts: [],
    officiant: "",
    readings: [""],
    music: [""],
    speakers: [""],
    pallbearers: [""],
    flowersOrDonations: "",
    dressCode: "",
    photosOrVideos: "",
    media: [],
    specialRequests: "",
    ashesLocation: "",
    graveInscription: "",
    ongoingWishes: "",
    roleTags: { speakers: [], pallbearers: [] },
    updated: null,
  };
}

function Accordion({ title, expanded, onToggle, children }) {
  return (
    <section className="accordion-section">
      <button type="button" className="accordion-header" onClick={onToggle}>
        <span style={{ display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
        {title}
      </button>
      {expanded && (<div className="accordion-body">{children}</div>)}
    </section>
  );
}

function Summary({ plan, contacts }) {
  const getName = (id) => contacts.find((c) => c.id === id)?.name || id;
  const joinNames = (arr) => (arr || []).map(getName).filter(Boolean).join(', ');
  const joinText = (arr) => (arr || []).filter((x) => String(x || "").trim()).join(', ');
  const rows = [
    ["Type of Service", plan.typeOfService],
    ["Venue/Location", plan.venue],
    ["Religious/Cultural Preferences", plan.religiousPrefs],
    ["People to Notify/Involve", joinNames(plan.notifyContacts)],
    ["Officiant", plan.officiant],
    ["Readings", joinText(plan.readings)],
    ["Music", joinText(plan.music)],
    ["Speakers", joinNames((plan.speakers || []).map(toId).filter(Boolean))],
    ["Pallbearers", joinNames((plan.pallbearers || []).map(toId).filter(Boolean))],
    ["Flowers/Donations", plan.flowersOrDonations],
    ["Dress Code", plan.dressCode],
    ["Photos / Video Messages", plan.photosOrVideos],
    ["Special Requests", plan.specialRequests],
    ["Ashes Location", plan.ashesLocation],
    ["Inscription", plan.graveInscription],
    ["Ongoing Wishes", plan.ongoingWishes],
  ].filter(([, val]) => String(val || "").trim().length > 0);
  return (
    <div className="card fp-summary-card">
      <h3 style={{ marginTop: 0, textAlign: 'left' }}>Your Funeral Plan (summary)</h3>
      {rows.length === 0 ? (
        <div>No details yet.</div>
      ) : (
        <div className="fp-summary-table" role="table">
          {rows.map(([label, val]) => (
            <div className="fp-summary-row" key={label} role="row">
              <div className="fp-summary-label" role="rowheader">{label}</div>
              <div className="fp-summary-value" role="cell">{val}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldList({ label, list, onChange, onAdd, onRemove }) {
  return (
    <div className="form-row">
      <label className="label">{label}</label>
      {(list || []).map((val, i) => (
        <div key={i} className="fl-row">
          <input className="input" type="text" value={val} onChange={(e) => onChange(i, e.target.value)} />
          <button type="button" className="btn btn-cancel" onClick={() => onRemove(i)}>–</button>
        </div>
      ))}
      <button type="button" className="btn btn-main" style={{ background: "#8cade1" }} onClick={onAdd}>+ Add</button>
    </div>
  );
}

function ContactRows({ title, value, onSetId, onSetNote, onAdd, onRemove, contacts, error, onNewContact }) {
  const items = Array.isArray(value) ? value : [];
  return (
    <div className="form-row">
      <label className="label">{title} {error && <span className="err">— {error}</span>}</label>
      {items.map((row, i) => (
        <div key={i} className="cr-row">
          <select
            className="input"
            value={typeof row === 'object' ? (row.id || "") : (row || "")}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__ADD__") { onNewContact(i); return; }
              onSetId(i, v);
            }}
          >
            <option value="">Select…</option>
            <option value="__ADD__">+ Add New Contact</option>
            {contacts.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <input className="input cr-note" placeholder="Note about this person’s role (optional)" value={typeof row === 'object' ? (row.note || "") : ""} onChange={(e) => onSetNote(i, e.target.value)} />
          <button type="button" className="btn btn-cancel" onClick={() => onRemove(i)}>–</button>
        </div>
      ))}
      <button type="button" className="btn btn-main" style={{ background: "#8cade1" }} onClick={onAdd}>+ Add</button>
    </div>
  );
}

function normalizeCategory(v) {
  const t = String(v || "").toLowerCase();
  if (/^family\b|\bfamily\b/.test(t)) return "Family";
  if (/^friend\b|\bfriends?\b/.test(t)) return "Friends";
  if (/\bwork\b|colleague|office|hr|manager/.test(t)) return "Work";
  if (/\bschool|college|university|teacher|principal/.test(t)) return "School/College";
  if (/\bcommunity|club|church|temple|mosque/.test(t)) return "Community";
  if (/\bneighbo?ur/.test(t)) return "Neighbourhood";
  return "Other";
}
function contactDisplayName(x){
  const nameParts = [x.firstName, x.lastName].filter(Boolean).join(" ").trim();
  return x.name || x.fullName || x.displayName || nameParts || x.email || x.phone || "Unnamed Contact";
}

export default function FuneralPlanning({ user }) {
  const { readOnly } = useAccess() || { readOnly: false }; // NEW

  const [plan, setPlan] = useState(emptyPlan(user?.uid));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState(0);
  const [template, setTemplate] = useState("");

  const [filterCategory, setFilterCategory] = useState("All");
  const [search, setSearch] = useState("");

  const [showGuideDrawer, setShowGuideDrawer] = useState(false);
  const [showDirectoryDrawer, setShowDirectoryDrawer] = useState(false);
  const [directoryIframeKey, setDirectoryIframeKey] = useState(0);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const mediaInputRef = useRef(null);
  const directoryIframeRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const [lastSavedSnap, setLastSavedSnap] = useState(null);
  const autosaveTimer = useRef(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (readOnly) setEditing(false);
  }, [readOnly]);

  // ✅ Remove the manager read-only overlay if present
  useEffect(() => {
    if (!readOnly || typeof document === "undefined") return;
    const selector = '[title="Read-only while managing another account"]';
    const removeMask = () => {
      document.querySelectorAll(selector).forEach((el) => { try { el.remove(); } catch {} });
    };
    removeMask();
    const mo = new MutationObserver(() => removeMask());
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch {}
    return () => { try { mo.disconnect(); } catch {} };
  }, [readOnly]);

  // Contacts from shared hook
  const { contacts: rawContacts } = useUserContacts(user);
  const contacts = useMemo(() => {
    const list = (rawContacts || []).map((x) => ({
      id: x.id,
      name: contactDisplayName(x),
      category: normalizeCategory(x.relationship || x.category || x.type),
      created: x.created?.toMillis?.() || x.createdAt?.toMillis?.() || x.created || 0,
    }));
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return list;
  }, [rawContacts]);

  // One-shot new-contact receiver
  const onPickRef = useRef(null);
  const contactUI = useAddContactAutoSelect(contacts, (created) => {
    if (onPickRef.current) { onPickRef.current(created); onPickRef.current = null; }
  });

  const openContactModal = () => {
    if (typeof contactUI?.openModal === "function") contactUI.openModal();
    else if (typeof contactUI?.setOpen === "function") contactUI.setOpen(true);
  };
  const openLater = (fn) => { setTimeout(() => fn(), 0); };

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const ref = doc(db, "funeralPlans", user.uid);
      const d = await getDoc(ref);
      if (d.exists()) {
        const base = emptyPlan(user.uid);
        const data = d.data();
        const merged = { ...base, ...data };
        merged.readings = Array.isArray(merged.readings) ? merged.readings : [String(merged.readings || "")];
        merged.music = Array.isArray(merged.music) ? merged.music : [String(merged.music || "")];
        merged.speakers = Array.isArray(merged.speakers) ? merged.speakers : [""];        
        merged.pallbearers = Array.isArray(merged.pallbearers) ? merged.pallbearers : [""];        
        merged.media = Array.isArray(merged.media) ? merged.media : [];
        merged.roleTags = merged.roleTags || { speakers: [], pallbearers: [] };
        merged.notifyContacts = Array.isArray(merged.notifyContacts) ? merged.notifyContacts : [];
        setPlan(merged);
        setLastSavedSnap(JSON.parse(JSON.stringify(merged)));
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    const total = 10; let filled = 0;
    if (plan.typeOfService) filled++;
    if (plan.venue) filled++;
    if (plan.religiousPrefs) filled++;
    if ((plan.notifyContacts || []).length) filled++;
    if (plan.officiant) filled++;
    if ((plan.readings || []).filter(Boolean).length) filled++;
    if ((plan.music || []).filter(Boolean).length) filled++;
    if ((plan.speakers || []).some((v) => toId(v))) filled++;
    if ((plan.pallbearers || []).some((v) => toId(v))) filled++;
    if ((plan.media || []).length) filled++;
    setProgress(Math.round((filled / total) * 100));
  }, [plan]);

  // savePlan wrapped to satisfy deps
  const savePlan = useCallback(async ({ silent = false } = {}) => {
    if (readOnly) return;
    if (!user?.uid) return;
    if (!silent) setSaving(true);
    try {
      const ref = doc(db, "funeralPlans", user.uid);
      const payload = { ...plan, uid: user.uid, updated: serverTimestamp() };
      await setDoc(ref, payload, { merge: true });
      const { updated, ...rest } = payload;
      setLastSavedSnap(JSON.parse(JSON.stringify(rest)));
    } catch (e) {
      if (!silent) alert("Save failed: " + (e?.message || String(e)));
    } finally {
      if (!silent) setSaving(false);
    }
  }, [user?.uid, plan, readOnly]);

  useEffect(() => {
    if (!editing) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => savePlan({ silent: true }), 1500);
    return () => clearTimeout(autosaveTimer.current);
  }, [plan, editing, savePlan]);

  function undoChanges() {
    if (readOnly) return;
    if (lastSavedSnap) setPlan(JSON.parse(JSON.stringify(lastSavedSnap)));
  }

  function validate() {
    const e = {};
    if (!plan.typeOfService) e.typeOfService = "Please choose the type of service.";
    if ((plan.speakers || []).some((v) => v && typeof v === "object" && !v.id)) e.speakers = "Each speaker needs a contact.";
    if ((plan.pallbearers || []).some((v) => v && typeof v === "object" && !v.id)) e.pallbearers = "Each pallbearer needs a contact.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave(e) {
    e.preventDefault();
    if (readOnly) return;
    if (!validate()) return;
    await savePlan();
    setEditing(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleReset() {
    if (readOnly) return;
    if (window.confirm("Reset all fields to blank?")) setPlan(emptyPlan(user?.uid));
  }
  function handleExpand(section) { setExpanded((e) => ({ ...e, [section]: !e[section] })); }
  const [expanded, setExpanded] = useState({ basic: true, service: false, personal: false, aftercare: false });

  const filteredContacts = useMemo(() => {
    const term = (search || "").toLowerCase();
    return contacts.filter((c) => {
      const catOk = filterCategory === "All" || c.category === filterCategory;
      const txt = `${c.name} ${c.category}`.toLowerCase();
      const termOk = !term || txt.includes(term);
      return catOk && termOk;
    });
  }, [contacts, filterCategory, search]);

  const groupedLeft = useMemo(
    () => groupContactsForOptions(filteredContacts.map((c) => ({ ...c, relationship: c.category }))),
    [filteredContacts]
  );

  const inNotify = useMemo(() => new Set(plan.notifyContacts || []), [plan.notifyContacts]);
  function addNotify(id) {
    if (readOnly) return;
    setPlan((p) => (inNotify.has(id) ? p : { ...p, notifyContacts: [...(p.notifyContacts || []), id] }));
  }
  function removeNotify(id) {
    if (readOnly) return;
    setPlan((p) => ({ ...p, notifyContacts: (p.notifyContacts || []).filter((x) => x !== id) }));
  }

  async function addMediaFiles(files) {
    if (readOnly) return;
    if (!files || !files.length || !user?.uid) return;
    const good = [];
    for (const f of files) {
      if (!MEDIA_ACCEPT.test(f.type)) { alert(`Unsupported file: ${f.name}`); continue; }
      if (f.size > MEDIA_MAX_SIZE) { alert(`Too large (max 50MB): ${f.name}`); continue; }
      good.push(f);
    }
    if (!good.length) return;
    const ref = doc(db, "funeralPlans", user.uid);
    const uploaded = [];
    setUploading(true);
    try {
      for (const f of good) {
        const path = `funeralPlans/${user.uid}/media/${Date.now()}_${f.name}`;
        const sref = storageRef(storage, path);
        const task = uploadBytesResumable(sref, f);
        await new Promise((res, rej) => task.on("state_changed", null, rej, res));
        const url = await getDownloadURL(task.snapshot.ref);
        uploaded.push({ name: f.name, url, type: f.type || "", time: Date.now(), path, caption: "" });
      }
      const next = [ ...(plan.media || []), ...uploaded ];
      setPlan((p) => ({ ...p, media: next }));
      await setDoc(ref, { media: next, updated: serverTimestamp() }, { merge: true });
    } catch (err) {
      alert("Upload failed: " + (err?.message || String(err)));
    } finally {
      setUploading(false);
      // Reset the upload input explicitly so ESLint doesn't flag a short-circuit expression
      if (mediaInputRef.current) {
        mediaInputRef.current.value = "";
      }
    }
  }

  async function removeMediaItem(idx) {
    if (readOnly) return;
    const item = (plan.media || [])[idx]; if (!item) return;
    if (!window.confirm(`Remove "${item.name}"?`)) return;
    try { await deleteObject(storageRef(storage, item.path)); } catch {}
    const next = [...(plan.media || [])]; next.splice(idx, 1);
    setPlan((p) => ({ ...p, media: next }));
    const ref = doc(db, "funeralPlans", user.uid);
    await setDoc(ref, { media: next, updated: serverTimestamp() }, { merge: true });
  }

  function mediaDragStart(e, idx) { if (readOnly) return; e.dataTransfer.setData("text/plain", String(idx)); }
  function mediaDragOver(e) { if (readOnly) return; e.preventDefault(); }
  async function mediaDrop(e, idx) {
    e.preventDefault();
    if (readOnly) return;
    const from = Number(e.dataTransfer.getData("text/plain"));
    if (Number.isNaN(from) || from === idx) return;
    const list = [...(plan.media || [])];
    const [m] = list.splice(from, 1);
    list.splice(idx, 0, m);
    setPlan((p) => ({ ...p, media: list }));
    const ref = doc(db, "funeralPlans", user.uid);
    await setDoc(ref, { media: list, updated: serverTimestamp() }, { merge: true });
  }

  function setMediaCaption(idx, caption) {
    if (readOnly) return;
    setPlan((p) => {
      const list = [...(p.media || [])];
      if (list[idx]) list[idx] = { ...list[idx], caption };
      return { ...p, media: list };
    });
  }

  const setRowVal = (arrName, idx, id) => {
    if (readOnly) return;
    setPlan((p) => {
      const arr = [...(p[arrName] || [])];
      const prev = arr[idx]; const prevNote = noteOf(prev);
      arr[idx] = id ? { id, note: prevNote } : "";
      return { ...p, [arrName]: arr };
    });
  };
  const setRowNote = (arrName, idx, note) => {
    if (readOnly) return;
    setPlan((p) => {
      const arr = [...(p[arrName] || [])]; const id = toId(arr[idx]);
      arr[idx] = id ? { id, note } : ""; return { ...p, [arrName]: arr };
    });
  };
  function addRow(arrName) {
    if (readOnly) return;
    setPlan((p) => ({ ...p, [arrName]: [ ...(p[arrName] || []), "" ] }));
  }
  function removeRow(arrName, idx) {
    if (readOnly) return;
    setPlan((p) => {
      const arr = [...(p[arrName] || [])];
      arr.splice(idx, 1);
      return { ...p, [arrName]: arr };
    });
  }

  function applyTemplate(name) {
    if (readOnly) return;
    const t = (name || template); if (!t) return;
    const presets = {
      Humanist: { typeOfService: "Celebration of Life", readings: ["A Song of Living (Amelia Burr)", "Do Not Stand at My Grave and Weep (Mary Frye)", ""], music: ["Somewhere Over the Rainbow — Israel K", "What a Wonderful World — Louis Armstrong", ""], religiousPrefs: "Humanist / Non-religious" },
      Catholic: { typeOfService: "Funeral Mass", readings: ["Wisdom 3:1-9", "Romans 6:3-9", "John 14:1-6"], music: ["Ave Maria", "Be Not Afraid", "On Eagle’s Wings"], religiousPrefs: "Catholic" },
      "Non-religious": { typeOfService: "Cremation Service", readings: ["The Dash (Linda Ellis)", "Afterglow (Helen Lowrie Marshall)"], music: ["Fix You — Coldplay", "See You Again — Wiz Khalifa"], religiousPrefs: "None" },
    };
    const patch = presets[t]; if (!patch) return; setPlan((p) => ({ ...p, ...patch }));
  }

  // Print & PDF from hidden node
  function printDoc() {
    const node = printRef.current; if (!node) return;
    const prev = { display: node.style.display };
    node.style.display = "block";
    const after = () => { node.style.display = prev.display; window.removeEventListener("afterprint", after); };
    window.addEventListener("afterprint", after);
    window.print();
  }
  async function downloadPdf() {
    try {
      const { jsPDF } = await ensurePdfLibs();
      const node = printRef.current; if (!node) return;
      const prev = { display: node.style.display };
      node.style.display = "block";
      const docPdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      await docPdf.html(node, { margin: 24, windowWidth: 780, autoPaging: "text", html2canvas: { scale: 0.95, useCORS: true } });
      docPdf.save("funeral-plan.pdf");
      node.style.display = prev.display;
    } catch (e) { alert("PDF export failed: " + (e?.message || String(e))); }
  }

  const forwardByEmail = () => {
    const txt = planToText(plan, contacts);
    const subject = encodeURIComponent("Funeral Plan");
    const body = encodeURIComponent(txt);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const resetDirectorySearch = useCallback(() => {
    setDirectoryLoading(true);
    setDirectoryIframeKey((k) => k + 1);
  }, []);

  const startNewDirectorySearch = useCallback(() => {
    resetDirectorySearch();
  }, [resetDirectorySearch]);

  const directoryDrawerBody = (
    <div className="fp-drawer-iframe-wrap">
      {directoryLoading && (
        <div className="fp-directory-loading" aria-live="polite" aria-label="Directory loading">
          <div className="fp-directory-loading__text">Directory loading, please wait…</div>
        </div>
      )}
      <iframe
        key={directoryIframeKey}
        ref={directoryIframeRef}
        title="Find a Funeral Specialist"
        src={DIRECTORY_URL}
        className="fp-drawer-iframe"
        onLoad={() => setDirectoryLoading(false)}
        loading="lazy"
        sandbox="allow-forms allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
      />
      <div className="fp-drawer-iframe-footer">
  <button
    type="button"
    className="btn fp-directory-new-search"
    onClick={startNewDirectorySearch}
    style={{ background: "#f97316", borderColor: "#f97316", color: "#fff" }}
  >
    <FiRefreshCw aria-hidden className="fp-directory-return__icon" />
    <span>Start a New Search</span>
  </button>
</div>

    </div>
  );

  const DirectoryDrawerButton = () => (
    <button
      type="button"
      className="btn btn-main fp-directory-btn"
      onClick={() => { setDirectoryLoading(true); setShowDirectoryDrawer(true); }}
      data-allow-readonly
    >
      <FiSearch className="fp-directory-btn__icon" aria-hidden />
      <span>Find a Funeral Specialist</span>
    </button>
  );

  const Actions = () => (
    <div className="dp-help-row" data-allow-readonly>
      <button type="button" className="btn dp-help read" onClick={() => setShowGuideDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico"/> Read how it works
      </button>
      <button type="button" className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico"/> See how it works
      </button>
    </div>
  );

  return (
    <div className="container fp-root">
      <div className="header-row">
        <h2 className="section-title" style={{ margin: 0 }}>Funeral Planning</h2>
        <div className="header-actions desktop-only"><Actions /></div>
      </div>

      <div className="hero-wrap"><img src="/lastwish-account-funeral.jpg" alt="Funeral" className="hero-img" /></div>
      <div className="fp-directory-row"><DirectoryDrawerButton /></div>
      <div className="mobile-only" style={{ margin: "10px 0" }}><Actions /></div>

      <div className="intro-box">
        <div className="fp-progress" role="img" aria-label={`Funeral plan ${progress}% complete`}>
          <div className="fp-progress__bar" style={{ width: `${progress}%` }} />
        </div>
        <div className="fp-progress__text" aria-live="polite">{progress}% complete</div>
        
      </div>

      {(readOnly || (!editing && plan.updated)) && (
        <>
          <Summary plan={plan} contacts={contacts} />
          <div className="form-actions fp-actions">
            {!readOnly && (
              <>
                <button className="btn btn-main" onClick={() => setEditing(true)}>Edit Funeral Plan</button>
                <button className="btn btn-secondary" type="button" onClick={printDoc}>Print</button>
                <button className="btn" type="button" onClick={downloadPdf}>Download PDF</button>
              </>
            )}
          </div>
        </>
      )}

      {!readOnly && (!plan.updated || editing) && (
        <form className="card fp-form" onSubmit={handleSave}>
          {/* Templates (restored) */}
          <div className="fp-templates">
            <label className="label">Apply Template</label>
            <div className="fp-templates__row">
              <select className="input" value={template} onChange={(e) => setTemplate(e.target.value)}>
                <option value="">Choose…</option>
                <option>Humanist</option>
                <option>Catholic</option>
                <option>Non-religious</option>
              </select>
              <button type="button" className="btn" onClick={() => applyTemplate()}>Apply</button>
            </div>
          </div>

          {/* 1) Basic Details */}
          <Accordion title="Basic Details" expanded={expanded.basic} onToggle={() => handleExpand("basic")}>
            <div className="form-row">
              <label className="label">Type of Service {errors.typeOfService && <span className="err">— {errors.typeOfService}</span>}</label>
              <select className="input" value={plan.typeOfService} onChange={(e) => setPlan({ ...plan, typeOfService: e.target.value })} required>
                <option value="">Select…</option>
                <option>Burial</option>
                <option>Cremation</option>
                <option>Natural/Green Burial</option>
                <option>Celebration of Life</option>
                <option>Cremation Service</option>
                <option>No Preference/Not Sure</option>
              </select>
            </div>

            <div className="form-row"><label className="label">Preferred Venue or Location</label><input className="input" value={plan.venue} onChange={(e) => setPlan({ ...plan, venue: e.target.value })} placeholder="e.g., St. Mary’s Church" /></div>
            <div className="form-row"><label className="label">Religious/Cultural Preferences</label><input className="input" value={plan.religiousPrefs} onChange={(e) => setPlan({ ...plan, religiousPrefs: e.target.value })} placeholder="e.g., Catholic, Humanist, None" /></div>

            <div className="form-row">
              <label className="label">People to Notify/Involve</label>
              <div className="notify-grid">
                <div className="notify-left">
                  <div className="fp-contact-filters">
                    <select className="input" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                      {CONTACT_CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                    <input className="input" placeholder="Search contacts…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    <button
                      type="button"
                      className="btn btn-main"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPickRef.current = (c) => addNotify(c.id);
                        openLater(openContactModal);
                      }}
                      title="Add a new contact"
                    >
                      + New Contact
                    </button>
                  </div>

                  <div className="notify-list">
                    {groupedLeft.map((g) => (
                      g.items.length ? (
                        <div key={g.key} className="notify-group">
                          <div className="notify-group-title">{g.label}</div>
                          <ul className="notify-items">
                            {g.items.map((c) => {
                              const selected = inNotify.has(c.id);
                              return (
                                <li key={c.id} className={"notify-item" + (selected ? " is-selected" : "") }>
                                  <span className="notify-name">{c.name}</span>
                                  <button type="button" className="btn btn-mini" disabled={selected} onClick={() => addNotify(c.id)}>+</button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : null
                    ))}
                  </div>
                </div>

                <div className="notify-right">
                  <div className="notify-right-head">Selected ({plan.notifyContacts.length})</div>
                  {plan.notifyContacts.length === 0 ? (
                    <div className="notify-empty">No one selected yet.</div>
                  ) : (
                    <ul className="notify-selected">
                      {plan.notifyContacts.map((id) => {
                        const name = contacts.find((c) => c.id === id)?.name || id;
                        return (
                          <li key={id} className="notify-selected-row">
                            <span className="notify-name">{name}</span>
                            <button type="button" className="btn btn-mini btn-cancel" onClick={() => removeNotify(id)}>–</button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </Accordion>

          {/* 2) Service Details */}
          <Accordion title="Service Details" expanded={expanded.service} onToggle={() => handleExpand("service")}>
            <div className="form-row"><label className="label">Officiant</label><input className="input" value={plan.officiant} onChange={(e) => setPlan({ ...plan, officiant: e.target.value })} placeholder="Name of person" /></div>

            <FieldList label="Readings, Poems or Texts" list={plan.readings} onChange={(i, v) => setPlan({ ...plan, readings: setAt(plan.readings, i, v) })} onAdd={() => setPlan({ ...plan, readings: [...plan.readings, ""] })} onRemove={(i) => setPlan({ ...plan, readings: removeAt(plan.readings, i) })} />
            <FieldList label="Preferred Music" list={plan.music} onChange={(i, v) => setPlan({ ...plan, music: setAt(plan.music, i, v) })} onAdd={() => setPlan({ ...plan, music: [...plan.music, ""] })} onRemove={(i) => setPlan({ ...plan, music: removeAt(plan.music, i) })} />

            <ContactRows
              title="Speakers"
              value={plan.speakers}
              onSetId={(i, id) => setRowVal("speakers", i, id)}
              onSetNote={(i, n) => setRowNote("speakers", i, n)}
              onAdd={() => addRow("speakers")}
              onRemove={(i) => removeRow("speakers", i)}
              contacts={contacts}
              error={errors.speakers}
              onNewContact={(i) => {
                onPickRef.current = (c) => setRowVal("speakers", i, c.id);
                openLater(openContactModal);
              }}
            />

            <ContactRows
              title="Pallbearers"
              value={plan.pallbearers}
              onSetId={(i, id) => setRowVal("pallbearers", i, id)}
              onSetNote={(i, n) => setRowNote("pallbearers", i, n)}
              onAdd={() => addRow("pallbearers")}
              onRemove={(i) => removeRow("pallbearers", i)}
              contacts={contacts}
              error={errors.pallbearers}
              onNewContact={(i) => {
                onPickRef.current = (c) => setRowVal("pallbearers", i, c.id);
                openLater(openContactModal);
              }}
            />

            <div className="form-row"><label className="label">Flowers or Donations</label><input className="input" value={plan.flowersOrDonations} onChange={(e) => setPlan({ ...plan, flowersOrDonations: e.target.value })} placeholder="Charity name or note" /></div>
          </Accordion>

          {/* 3) Personal Touches */}
          <Accordion title="Personal Touches" expanded={expanded.personal} onToggle={() => handleExpand("personal")}>
            <div className="form-row"><label className="label">Dress Code</label><input className="input" value={plan.dressCode} onChange={(e) => setPlan({ ...plan, dressCode: e.target.value })} placeholder="e.g., Bright colours" /></div>

            <div className="form-row"><label className="label">Notes about Photos or Video Messages</label><textarea className="input" rows={2} value={plan.photosOrVideos} onChange={(e) => setPlan({ ...plan, photosOrVideos: e.target.value })} placeholder="Any context you want to add" /></div>

            <MediaSection
              plan={plan}
              setPlan={setPlan}
              mediaInputRef={mediaInputRef}
              uploading={uploading}
              addMediaFiles={addMediaFiles}
              lightbox={lightbox}
              setLightbox={setLightbox}
              removeMediaItem={removeMediaItem}
              mediaDragStart={mediaDragStart}
              mediaDragOver={mediaDragOver}
              mediaDrop={mediaDrop}
              setMediaCaption={setMediaCaption}
            />
          </Accordion>

          {/* 4) Aftercare & Remembrance */}
          <Accordion title="Aftercare & Remembrance" expanded={expanded.aftercare} onToggle={() => handleExpand("aftercare")}>
            <div className="form-row"><label className="label">Ashes Location</label><input className="input" value={plan.ashesLocation} onChange={(e) => setPlan({ ...plan, ashesLocation: e.target.value })} /></div>
            <div className="form-row"><label className="label">Grave/Memorial Inscription</label><input className="input" value={plan.graveInscription} onChange={(e) => setPlan({ ...plan, graveInscription: e.target.value })} /></div>
            <div className="form-row"><label className="label">Ongoing Wishes</label><input className="input" value={plan.ongoingWishes} onChange={(e) => setPlan({ ...plan, ongoingWishes: e.target.value })} /></div>
          </Accordion>

          <div className="form-actions fp-actions">
            <button type="submit" className="btn btn-main" disabled={saving}>{saving ? "Saving…" : "Save Funeral Plan"}</button>
            <button type="button" className="btn" onClick={undoChanges} disabled={!lastSavedSnap}>Undo Changes</button>
            <button type="button" className="btn btn-cancel" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-cancel" onClick={handleReset} disabled={saving}>Reset All</button>
          </div>
        </form>
      )}

      {lightbox && (
        <div className="modal-bg" onClick={() => setLightbox(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="modal-body">
              {/ˆimage\//i.test(lightbox.type || "") ? (
                <img src={lightbox.url} alt="" style={{ width: "100%", height: "auto", borderRadius: 10 }} />
              ) : /^video\//i.test(lightbox.type || "") ? (
                <video src={lightbox.url} controls style={{ width: "100%", borderRadius: 10 }} />
              ) : (
                <a href={lightbox.url} target="_blank" rel="noreferrer">Open file</a>
              )}
              <div className="form-actions"><button className="btn btn-cancel" type="button" onClick={() => setLightbox(null)}>Close</button></div>
            </div>
          </div>
        </div>
      )}

      <div ref={printRef} style={{ display: "none" }}>
        <div style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, sans-serif', color: '#111827', maxWidth: 780, margin: '0 auto', textAlign: 'left' }}>
          <h1>Funeral Plan</h1>
          <p><b>Generated:</b> {new Date().toLocaleString()}</p>
          <p><b>People to Notify/Involve:</b> {(plan.notifyContacts || []).map((id) => contacts.find((c) => c.id === id)?.name || id).join(', ')}</p>
        </div>
      </div>

      {/* Universal Add-Contact modal */}
      <AddContactModal open={contactUI.open} onClose={() => contactUI.setOpen(false)} user={user} />

      {/* ✅ Bottom actions row — read-only only */}
      {readOnly && (
        <div className="form-actions fp-actions" style={{ marginTop: 14 }} data-allow-readonly>
          <button className="btn" type="button" onClick={downloadPdf} data-allow-readonly>Download PDF</button>
          <button className="btn btn-secondary" type="button" onClick={printDoc} data-allow-readonly>Print</button>
          <button className="btn btn-cancel" type="button" onClick={forwardByEmail} data-allow-readonly>Forward</button>
        </div>
      )}

      {/* Help video + drawer */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/dJnNcq6EhnU?si=SSiQ3MYPHIyGGupS"
        title="How the Funeral Planning page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {showGuideDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowGuideDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Funeral Planning page works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>Print</button>
                <button className="btn btn-cancel" onClick={() => setShowGuideDrawer(false)} type="button" data-allow-readonly>Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <ol>
                <li>Add key wishes (service type, venue, readings, music).</li>
                <li>Select people to notify/involve from your Contacts.</li>
                <li>Upload photos or short video messages if you want.</li>
                <li>Use <b>Save</b> often; managers can only view, print, download, or forward.</li>
              </ol>
            </div>
          </aside>
        </div>
      )}

        {showDirectoryDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDirectoryDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">Find a Funeral Specialist</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>Print</button>
                <button className="btn btn-cancel" onClick={() => setShowDirectoryDrawer(false)} type="button" data-allow-readonly>Close</button>
              </div>
            </div>
            <div className="dp-drawer-body dp-drawer-body--directory">{directoryDrawerBody}</div>
          </aside>
        </div>
      )}

      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}
        .desktop-only{display:none} .mobile-only{display:block}
        @media(min-width:900px){.desktop-only{display:block}.mobile-only{display:none}}
        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}
        @media (max-width: 700px){
          .hero-img{height:110px}
        }
        .fp-directory-row{margin:6px 0 14px}
        .fp-directory-btn{width:100%;background:#f97316;border-color:#f97316;display:inline-flex;align-items:center;justify-content:center;gap:10px;text-transform:none}
        .fp-directory-btn__icon{font-size:1.1rem}
        .fp-drawer-iframe-wrap{position:relative;display:flex;flex-direction:column;height:100%;min-height:0;gap:0}
        .fp-drawer-iframe{border:0;width:100%;flex:1;min-height:0;height:100%}
        .fp-drawer-iframe-footer{flex:0 0 auto;display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end;background:#fff;border-top:1px solid #eee;padding:10px 12px;padding-bottom:max(12px, env(safe-area-inset-bottom))}
        .fp-directory-return,.fp-directory-new-search{flex:1 1 200px;gap:10px;display:inline-flex;align-items:center;justify-content:center}
        .fp-directory-return__icon{font-size:1.1rem}
.fp-directory-loading{position:absolute;inset:0;display:grid;place-items:center;background:rgba(255,255,255,.75);backdrop-filter:saturate(120%) blur(1px);z-index:2;pointer-events:none}
        .fp-directory-loading__text{font-weight:900;color:#980000;letter-spacing:.2px;animation:fpPulse 1.2s ease-in-out infinite}
        @keyframes fpPulse{0%,100%{opacity:.35;transform:scale(.98)}50%{opacity:1;transform:scale(1)}}
        .dp-help-row{display:flex;gap:10px;flex-wrap:wrap}
        .dp-help{border-radius:8px;padding:10px 14px;display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#fff;border:none}
        .dp-help.read{background:#650202;color:#fff}
        .dp-help.see{background:#003353;color:#fff}
        .dp-help-ico{font-size:1.05rem}
        .fp-root .mobile-only .dp-help-row{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
        .fp-root .mobile-only .dp-help{width:100%;justify-content:center}
        .container{max-width:760px;margin:0 auto;padding:20px;display:flex;flex-direction:column}
        .intro-box{background:#f5f1f5;border-radius:12px;padding:16px;margin-bottom:20px;color:#523053}
        .intro-box strong{display:block;margin-bottom:8px;font-size:1.1em}
        .fp-progress{position:relative;height:10px;background:#ffe1d6;border-radius:999px;overflow:hidden}
        .fp-progress__bar{position:absolute;left:0;top:0;height:100%;background:linear-gradient(90deg,#f15822,#980000);border-radius:999px;transition:width .3s ease}
        .fp-progress__text{margin-top:8px;font-size:14px;font-weight:600;color:#980000}
        .fp-root .section-title{color:#980000;border-bottom-color:#f15822}
        .card{background:#fff;border-radius:10px;box-shadow:0 2px 10px rgba(0,0,0,0.05);padding:20px;margin-bottom:24px;display:flex;flex-direction:column}
        .form-row{margin-bottom:16px;display:flex;flex-direction:column}
        .label{margin-bottom:6px;font-weight:600;color:#6b7280}
        .input{width:100%;padding:10px 12px;border:1px solid #d6d6e7;border-radius:10px;font-size:1em;background:#fff}
        .input:focus{border-color:#8fa0ff;box-shadow:0 0 0 4px rgba(79,70,229,.12)}
        .err{color:#b91c1c;font-weight:700}
        .form-actions{display:flex;gap:12px;margin-top:20px;flex-wrap:wrap}
        .btn{border:none;border-radius:10px;font-weight:700;cursor:pointer;padding:10px 18px}
        .btn-main{background:#f15822;color:#fff}
        .btn-secondary{background:#8cade1;color:#fff}
        .btn-cancel{background:#e5e7eb;color:#333}
        .btn-mini{height:32px;padding:0 10px;border-radius:8px;border:1px solid #d1d5db;background:#fff;font-weight:800;cursor:pointer}
        .accordion-section{margin-bottom:20px;border-bottom:1px solid #eee}
        .accordion-header{background:none;border:none;font-size:1.05em;font-weight:700;display:flex;align-items:center;cursor:pointer;padding:6px 0}
        .accordion-header span{margin-right:8px;transition:transform .2s}
        .fp-contact-filters{display:grid;grid-template-columns:160px 1fr auto;gap:8px;margin-bottom:8px}
        @media(max-width:640px){.fp-contact-filters{grid-template-columns:1fr 1fr}}
        .notify-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        @media(max-width:760px){.notify-grid{grid-template-columns:1fr}}
        .notify-left,.notify-right{border:1px solid #ececf5;border-radius:10px;padding:10px;background:#fafafa}
        .notify-list{display:grid;gap:10px;max-height:320px;overflow:auto}
        .notify-group-title{font-weight:800;color:#2a0516;opacity:.9;font-size:13px;margin:6px 0}
        .notify-items{list-style:none;margin:0;padding:0;display:grid;gap:6px}
        .notify-item{display:grid;grid-template-columns:1fr auto;align-items:center;gap:8px;background:#fff;border:1px solid #e5e7eb;padding:8px}
        .notify-item.is-selected{opacity:.6}
        .notify-right-head{font-weight:800;margin-bottom:6px}
        .notify-selected{list-style:none;margin:0;padding:0;display:grid;gap:6px}
        .notify-selected-row{display:grid;grid-template-columns:1fr auto;align-items:center;gap:8px;background:#fff;border:1px solid #e5e7eb;padding:8px}
        .notify-empty{color:#6b7280}
        .fl-row{display:flex;gap:8px;margin-bottom:8px}
        .fl-row .input{flex:1}
        .cr-row{display:grid;grid-template-columns:1fr 1fr auto;gap:8px;align-items:center;margin-bottom:8px}
        @media(max-width:640px){ .cr-row{grid-template-columns:1fr} }
        .cr-row .cr-note{min-height:42px}
        .media-list{display:grid;gap:10px}
        .media-row{display:grid;grid-template-columns:auto 1fr auto;gap:10px;align-items:center;border:1px dashed #e5e7eb;border-radius:10px;padding:8px;background:#fff}
        .media-thumb{width:80px;height:60px;border-radius:8px;overflow:hidden;position:relative;display:grid;place-items:center;background:#f3f4f6;cursor:pointer}
        .media-thumb img,.media-thumb video{width:100%;height:100%;object-fit:cover;display:block}
        .media-generic{font-size:20px}
        .media-caption{min-height:42px}
        .badge{position:absolute;right:6px;bottom:6px;background:rgba(0,0,0,.6);color:#fff;font-size:12px;padding:2px 6px;border-radius:999px}
        .media-remove{padding:0 10px}
        .modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;z-index:1000}
        .modal-card{background:#fff;border-radius:12px;padding:20px;max-width:560px;width:95vw}
       .fp-summary-table{display:grid;gap:12px;margin-top:12px}
        .fp-summary-row{display:flex;flex-direction:column;gap:6px;padding:12px 16px;border-radius:12px;background:#fdfbff;border:1px solid #ede9fe}
        .fp-summary-label{font-weight:700;color:#4c1d95}
        .fp-summary-value{color:#1f2937}
        @media(min-width:640px){
          .fp-summary-row{flex-direction:row;align-items:center}
          .fp-summary-label{flex:1}
          .fp-summary-value{flex:1;text-align:right}
        }
        /* Drawer */
        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:2147483000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;bottom:0;height:auto;width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden;z-index:2147483001}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto;flex:1;min-height:0;padding-bottom:max(16px, env(safe-area-inset-bottom))}
        .dp-drawer-body--directory{padding:0 !important;overflow:hidden !important;display:flex !important;flex-direction:column !important;min-height:0 !important}

        /* Mobile: keep drawer above the fixed footer area */
        @media (max-width:700px){
          .dp-drawer-bg{align-items:flex-end}
          .dp-drawer{
            top:auto !important;
            height:75vh !important;
            /* If your mobile footer height differs, adjust 84px */
            bottom:calc(84px + env(safe-area-inset-bottom)) !important;
          }
        }
      `}</style>
    </div>
  );
}

function MediaSection({
  plan, setPlan, mediaInputRef, uploading, addMediaFiles, lightbox, setLightbox,
  removeMediaItem, mediaDragStart, mediaDragOver, mediaDrop, setMediaCaption,
}) {
  return (
    <div className="form-row">
      <label className="label">Photos or Video Messages</label>
      <div className="media-list">
        {(plan.media || []).map((m, i) => {
          const isImg = /^image\//i.test(m.type || "");
          const isVid = /^video\//i.test(m.type || "");
          return (
            <div key={m.url} className="media-row" draggable onDragStart={(e) => mediaDragStart(e, i)} onDragOver={mediaDragOver} onDrop={(e) => mediaDrop(e, i)}>
              <div className="media-thumb" onClick={() => setLightbox({ url: m.url, type: m.type })} role="button" title="Open preview" data-allow-readonly>
                {isImg ? (<img src={m.url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />) : isVid ? (
                  <video src={m.url} preload="metadata" muted playsInline />
                ) : (<div className="media-generic">📎</div>)}
                {isVid && <span className="badge">🎬</span>}
              </div>
              <input className="input media-caption" placeholder="Add caption…" value={m.caption || ""} onChange={(e) => setMediaCaption(i, e.target.value)} />
              <button type="button" className="btn btn-cancel media-remove" onClick={() => removeMediaItem(i)}>–</button>
            </div>
          );
        })}
      </div>
      <input ref={mediaInputRef} type="file" multiple style={{ display: "none" }} onChange={(e) => addMediaFiles(e.target.files)} />
      <button
        type="button"
        className="btn btn-main"
        style={{ background: "#8cade1", marginTop: 6 }}
        onClick={() => {
          if (mediaInputRef.current) {
            mediaInputRef.current.click();
          }
        }}
        disabled={uploading}
      >
        {uploading ? "Uploading…" : "+ Add"}
      </button>
    </div>
  );
}
